import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import API_BASE from "../../../config";
import { canSeeMasterList } from "../../directory/StudentProfileModal/profileUtils";
import StudentProfileModal from "../../directory/StudentProfileModal/StudentProfileModal";
import { exportFinancialReport } from "./exportFinancialReport";
import FinancialCompareModal from "./FinancialCompareModal";
import RecentAnnouncementsWidget from "../../announcements/RecentAnnouncementsWidget";
import s from "./AdminDashboard.module.css";
import { exportFinanceDetailPdf } from "../../../utils/financeDetailPdf";

const AVATAR_COLORS = ["#5a4e3a","#3a4e4a","#4a3a5a","#4e4a3a","#3a4a5a","#5a3a3a","#3a5a3a"];
function avatarColor(name) {
  let h = 0;
  for (let i = 0; i < (name || "").length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
}
function initials(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "")).toUpperCase();
}

/** Richer admin landing page. Two live panels — Teaching Load and Subject
 *  Coverage — surface the gaps that would otherwise take half a dozen clicks
 *  across Academics tabs to uncover. Read-only; every "fix this" link drops
 *  the admin back into the appropriate Academics tab. */
export default function AdminDashboard({ user }) {
  // The finance snapshot is SuperAdmin/Admin-only — AcademicHead also lands
  // on this dashboard (shared academic layout) but has no business with
  // tuition numbers. SuperDev (highest-privilege role) inherits this too.
  const showFinance = user?.role === "Admin"
                   || user?.role === "SuperAdmin"
                   || user?.role === "SuperDev";

  const [schoolYears,  setSchoolYears]  = useState([]);
  const [semesters,    setSemesters]    = useState([]);
  const [sections,     setSections]     = useState([]);
  const [gradeLevels,  setGradeLevels]  = useState([]);
  const [subjects,     setSubjects]     = useState([]);
  const [offerings,    setOfferings]    = useState([]);
  const [employees,    setEmployees]    = useState([]);
  const [students,     setStudents]     = useState([]);
  const [payments,      setPayments]      = useState([]);
  const [invoices,      setInvoices]      = useState([]);
  const [enrollments,   setEnrollments]   = useState([]);
  const [feeStructures, setFeeStructures] = useState([]);
  const [expenses,      setExpenses]      = useState([]);
  const [loading,       setLoading]       = useState(true);

  const [syId,  setSyId]  = useState("");
  const [semId, setSemId] = useState("");

  useEffect(() => {
    let cancelled = false;
    // Finance fetches (payments / invoices / enrollments) are only requested
    // for roles that will see the finance snapshot — keeps AcademicHead from
    // paying the cost of three extra lookups they'll never read.
    const financeFetches = showFinance ? [
      fetch(`${API_BASE}/api/tuition-payments`).then(r => r.ok ? r.json() : []),
      fetch(`${API_BASE}/api/tuition-invoices`).then(r => r.ok ? r.json() : []),
      fetch(`${API_BASE}/api/enrollments`).then(r => r.ok ? r.json() : []),
      fetch(`${API_BASE}/api/fee-structures`).then(r => r.ok ? r.json() : []),
      fetch(`${API_BASE}/api/expenses`).then(r => r.ok ? r.json() : []),
    ] : [Promise.resolve([]), Promise.resolve([]), Promise.resolve([]), Promise.resolve([]), Promise.resolve([])];

    Promise.all([
      fetch(`${API_BASE}/api/school-years`).then(r => r.ok ? r.json() : []),
      fetch(`${API_BASE}/api/semesters`).then(r => r.ok ? r.json() : []),
      fetch(`${API_BASE}/api/sections`).then(r => r.ok ? r.json() : []),
      fetch(`${API_BASE}/api/grade-levels`).then(r => r.ok ? r.json() : []),
      fetch(`${API_BASE}/api/subjects`).then(r => r.ok ? r.json() : []),
      fetch(`${API_BASE}/api/class-offerings`).then(r => r.ok ? r.json() : []),
      fetch(`${API_BASE}/api/employees?all=true`).then(r => r.ok ? r.json() : []),
      fetch(`${API_BASE}/api/students`).then(r => r.ok ? r.json() : []),
      ...financeFetches,
    ]).then(([sy, sem, sec, gl, subj, off, emp, stu, pay, inv, enr, feeStr, exp]) => {
      if (cancelled) return;
      setSchoolYears(sy); setSemesters(sem); setSections(sec); setGradeLevels(gl);
      setSubjects(subj);  setOfferings(off); setEmployees(emp);
      setStudents(stu);
      setPayments(Array.isArray(pay) ? pay : []);
      setInvoices(Array.isArray(inv) ? inv : []);
      setEnrollments(Array.isArray(enr) ? enr : []);
      setFeeStructures(Array.isArray(feeStr) ? feeStr : []);
      setExpenses(Array.isArray(exp) ? exp : []);
      const activeSy  = sy.find(y => y.active) || sy[0];
      if (activeSy) setSyId(String(activeSy.id));
      const activeSem = sem.find(x => x.active) || sem.find(x => !activeSy || String(x.schoolYearId) === String(activeSy.id)) || sem[0];
      if (activeSem) setSemId(String(activeSem.id));
    }).catch(() => {}).finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [showFinance]);

  // When the admin changes SY, reset Semester to one that belongs to it.
  useEffect(() => {
    if (!syId) return;
    const stillValid = semesters.some(x => String(x.id) === semId && String(x.schoolYearId) === String(syId));
    if (stillValid) return;
    const inSy = semesters.filter(x => String(x.schoolYearId) === String(syId));
    const fresh = inSy.find(x => x.active) || inSy[0];
    setSemId(fresh ? String(fresh.id) : "");
  }, [syId, semId, semesters]);

  // ── Derived data ─────────────────────────────────────────────────────
  const employeeById    = useMemo(() => Object.fromEntries(employees.map(e => [e.id, e])), [employees]);
  const subjectById     = useMemo(() => Object.fromEntries(subjects.map(x => [x.id, x])), [subjects]);
  const sectionById     = useMemo(() => Object.fromEntries(sections.map(x => [x.id, x])), [sections]);
  const gradeLevelById  = useMemo(() => Object.fromEntries(gradeLevels.map(g => [g.id, g])), [gradeLevels]);
  const sectionFullLabel = (sec) => {
    if (!sec) return "—";
    const grade = gradeLevelById[sec.gradeLevelId];
    return grade ? `${grade.name} · ${sec.name}` : sec.name;
  };

  /** Every employee with staffType == TEACHING is treated as a teacher,
   *  regardless of whether they currently have any class offerings. */
  const teachers = useMemo(
    () => employees.filter(e => e.staffType === "TEACHING"),
    [employees]
  );

  /** Sections in the currently selected school year. */
  const sectionsInSY = useMemo(
    () => sections.filter(sec => !syId || String(sec.schoolYearId) === String(syId)),
    [sections, syId]
  );

  /** Offerings for the currently selected semester (and therefore SY). */
  const offeringsInSem = useMemo(
    () => offerings.filter(o => !semId || String(o.semesterId) === String(semId)),
    [offerings, semId]
  );

  /** Teacher → list of offerings (this semester). */
  const offeringsByTeacher = useMemo(() => {
    const map = new Map();
    for (const o of offeringsInSem) {
      if (o.teacherStaffId == null) continue;
      if (!map.has(o.teacherStaffId)) map.set(o.teacherStaffId, []);
      map.get(o.teacherStaffId).push(o);
    }
    return map;
  }, [offeringsInSem]);

  /** Subject → list of offerings (this semester). */
  const offeringsBySubject = useMemo(() => {
    const map = new Map();
    for (const o of offeringsInSem) {
      if (o.subjectId == null) continue;
      if (!map.has(o.subjectId)) map.set(o.subjectId, []);
      map.get(o.subjectId).push(o);
    }
    return map;
  }, [offeringsInSem]);

  // Ordered teacher list: unassigned first, then the rest alphabetically.
  const teachersSorted = useMemo(() => {
    return [...teachers].sort((a, b) => {
      const la = (offeringsByTeacher.get(a.id) || []).length;
      const lb = (offeringsByTeacher.get(b.id) || []).length;
      if ((la === 0) !== (lb === 0)) return la === 0 ? -1 : 1; // zero first
      return (a.name || "").localeCompare(b.name || "");
    });
  }, [teachers, offeringsByTeacher]);

  // Ordered subject list: uncovered first, then alphabetical by code.
  const subjectsSorted = useMemo(() => {
    return [...subjects].sort((a, b) => {
      const la = (offeringsBySubject.get(a.id) || []).length;
      const lb = (offeringsBySubject.get(b.id) || []).length;
      if ((la === 0) !== (lb === 0)) return la === 0 ? -1 : 1;
      return (a.code || "").localeCompare(b.code || "");
    });
  }, [subjects, offeringsBySubject]);

  // Stat tiles.
  const teachersUnassigned = teachersSorted.filter(t => (offeringsByTeacher.get(t.id) || []).length === 0).length;
  const subjectsUncovered  = subjectsSorted.filter(x => (offeringsBySubject.get(x.id) || []).length === 0).length;
  const gapCount           = teachersUnassigned + subjectsUncovered;
  const enrolledStudents   = students.filter(st => (st.status || "ENROLLED") === "ENROLLED").length;

  // ── Finance snapshot (Admin/SuperAdmin only) ─────────────────────────
  // All derived purely client-side from the payment/invoice/enrollment
  // lists we already fetched — no extra endpoints, no charting library.
  const monthStart = useMemo(() => {
    const d = new Date(); d.setDate(1); d.setHours(0, 0, 0, 0); return d.getTime();
  }, []);
  // Last millisecond of the current calendar month — used to decide whether
  // an invoice is "due this month or earlier" for the Unpaid KPI.
  const monthEnd = useMemo(() => {
    const d = new Date(); d.setMonth(d.getMonth() + 1, 1); d.setHours(0, 0, 0, 0);
    return d.getTime() - 1;
  }, []);
  const thirtyDaysAgo = useMemo(() => Date.now() - 30 * 24 * 60 * 60 * 1000, []);

  // Invoices that match the currently-selected School Year + Semester
  // filters. Every finance derivation below scopes through this set so the
  // snapshot always matches the filter pills the admin sees above.
  const scopedInvoices = useMemo(() => {
    return invoices.filter(inv => {
      if (syId  && String(inv.schoolYearId) !== String(syId))  return false;
      if (semId && String(inv.semesterId)   !== String(semId)) return false;
      return true;
    });
  }, [invoices, syId, semId]);

  const scopedInvoiceIds = useMemo(
    () => new Set(scopedInvoices.map(inv => inv.id)),
    [scopedInvoices],
  );

  /** Payments that belong to scoped invoices. Payments don't carry SY/Sem
   *  directly — they hang off an invoice, so we filter via the id set. */
  const scopedPayments = useMemo(
    () => payments.filter(p => scopedInvoiceIds.has(p.invoiceId)),
    [payments, scopedInvoiceIds],
  );

  /** Enrollments in the selected SY + Semester. */
  const scopedEnrollments = useMemo(() => {
    return enrollments.filter(e => {
      if (syId  && String(e.schoolYearId) !== String(syId))  return false;
      if (semId && String(e.semesterId)   !== String(semId)) return false;
      return true;
    });
  }, [enrollments, syId, semId]);

  const financeKPIs = useMemo(() => {
    if (!showFinance) return null;

    // This month's tuition collected — sum of payments actually RECEIVED
    // this calendar month (paymentReceivedOn, falling back to paidAt for
    // legacy rows with no received date recorded) AND within the selected
    // SY/Sem scope. Negative amounts (reversals/refunds) still net here
    // so the figure matches the till. Bounded on both ends — a payment
    // entered today but backdated to last month must NOT count, and (in
    // case a received date is ever mis-entered into the future) neither
    // should one dated past the end of this month.
    let thisMonthCollected = 0;
    for (const p of scopedPayments) {
      const t = p.paymentReceivedOn ? new Date(p.paymentReceivedOn).getTime()
        : (p.paidAt ? new Date(p.paidAt).getTime() : 0);
      if (t >= monthStart && t <= monthEnd) thisMonthCollected += Number(p.amount || 0);
    }

    // Outstanding receivables across scoped invoices — sum of
    // (invoice.amount − payments on it) where the difference is still
    // positive. Overpaid invoices don't subtract from the total.
    const paidByInvoice = new Map();
    for (const p of scopedPayments) {
      const k = p.invoiceId;
      paidByInvoice.set(k, (paidByInvoice.get(k) || 0) + Number(p.amount || 0));
    }
    let outstanding = 0;
    for (const inv of scopedInvoices) {
      const remaining = Number(inv.amount || 0) - (paidByInvoice.get(inv.id) || 0);
      if (remaining > 0.005) outstanding += remaining;
    }

    // New enrollments this month — calendar-month AND scoped SY/Sem.
    const newEnrollmentsThisMonth = scopedEnrollments.filter(e => {
      const t = e.enrolledAt ? new Date(e.enrolledAt).getTime() : 0;
      return t >= monthStart;
    }).length;

    // Unpaid / overdue count — UNPAID or PARTIAL scoped invoices with a
    // due date on or before end-of-current-month. Future-dated invoices
    // (next semester) don't belong here yet. Missing dueDate falls back
    // into the count so legacy rows aren't silently ignored.
    const nowMs = Date.now();
    let unpaidInvoices = 0;
    let overdueInvoices = 0;
    for (const inv of scopedInvoices) {
      const st = (inv.status || "").toUpperCase();
      if (st !== "UNPAID" && st !== "PARTIAL") continue;
      const dueMs = inv.dueDate ? new Date(inv.dueDate).getTime() : null;
      if (dueMs == null || dueMs <= monthEnd) {
        unpaidInvoices += 1;
        if (dueMs != null && dueMs < nowMs) overdueInvoices += 1;
      }
    }

    // ── Financial Overview metrics ────────────────────────────────────────
    // Build a grade+SY → undiscounted fee lookup from loaded fee structures.
    const feeByKey = new Map();
    for (const fs of feeStructures) {
      feeByKey.set(`${fs.gradeLevelId}|${fs.schoolYearId}`, Number(fs.totalAmount || 0));
    }
    const secMap = new Map(sections.map(x => [String(x.id), x]));
    const stuMap = new Map(students.map(x => [String(x.id), x]));

    // GRAND TOTAL: for each invoiced student in scope, sum their grade's
    // undiscounted fee structure amount. Falls back to invoice.amount when
    // the student has no current section or no matching fee structure.
    let grandTotal = 0;
    for (const inv of scopedInvoices) {
      const stu   = stuMap.get(String(inv.studentId));
      const sec   = stu?.currentSectionId ? secMap.get(String(stu.currentSectionId)) : null;
      const fsAmt = sec?.gradeLevelId ? feeByKey.get(`${sec.gradeLevelId}|${inv.schoolYearId}`) : null;
      grandTotal += fsAmt != null ? fsAmt : Number(inv.amount || 0);
    }

    // GROSS TOTAL: sum of invoice amounts (already reflect per-student discounts).
    const grossTotal = scopedInvoices.reduce((sum, inv) => sum + Number(inv.amount || 0), 0);

    // TOTAL PAYMENTS: sum of all payments on scoped invoices.
    const totalPayments = scopedPayments.reduce((sum, p) => sum + Number(p.amount || 0), 0);

    // TOTAL EXPENSES: sum of manually-recorded (non-voided) expenses only
    // — utilities, rent, supplies, etc. Deliberately NOT auto-blended with
    // the live Employee.salary payroll snapshot: the owner wants full
    // control over what counts here, so payroll only shows up if they
    // choose to log it themselves (there's a "Salaries & Wages" category
    // for exactly that).
    const totalExpenses = expenses
      .filter(e => e.status !== "VOIDED")
      .reduce((sum, e) => sum + Number(e.amount || 0), 0);

    // CURRENT DEPOSITS: what the school actually has in hand after expenses.
    const currentDeposits = totalPayments - totalExpenses;

    // REMAINING COLLECTIBLES: what students still owe. Intentionally the
    // same figure as `outstanding` above — both come from the identical
    // scoped invoice/payment sets, summed per-invoice so one student's
    // overpayment can never net against another student's unpaid balance.
    // (A single global `grossTotal - totalPayments` subtraction would let
    // that happen and silently drift from Outstanding Receivables.)
    const remainingCollectibles = outstanding;

    return {
      thisMonthCollected, outstanding, newEnrollmentsThisMonth,
      unpaidInvoices, overdueInvoices,
      grandTotal, grossTotal, totalPayments, totalExpenses,
      currentDeposits, remainingCollectibles,
      // Exposed so other derivations (Unpaid by Grade Level) don't have to
      // re-sum every scoped payment by invoice a second time.
      paidByInvoice,
    };
  }, [showFinance, scopedPayments, scopedInvoices, scopedEnrollments, monthStart, monthEnd,
      feeStructures, sections, students, expenses]);

  // Shared raw-data bundle for the Financial Overview + Finance Snapshot
  // info-modal breakdowns — one object so both panels' drill-downs stay
  // backed by the identical scoped data the headline figures use.
  const financeRawData = useMemo(() => ({
    invoices: scopedInvoices,
    payments: scopedPayments,
    allPayments: payments,
    employees,
    expenses,
    feeStructures,
    sections,
    students,
    gradeLevels,
    monthStart,
    monthEnd,
  }), [scopedInvoices, scopedPayments, payments, employees, expenses, feeStructures, sections, students, gradeLevels, monthStart, monthEnd]);

  // ── Unpaid by Grade Level ────────────────────────────────────────────
  // "YYYY-MM" of every month that has a scoped invoice due in it, plus the
  // current month (so the picker always has somewhere to land even before
  // any invoice for "now" exists). Sorted ascending.
  const unpaidMonthOptions = useMemo(() => {
    const set = new Set();
    for (const inv of scopedInvoices) {
      if (!inv.dueDate) continue;
      const d = new Date(inv.dueDate);
      if (isNaN(d.getTime())) continue;
      set.add(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
    }
    const now = new Date();
    set.add(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`);
    return [...set].sort();
  }, [scopedInvoices]);

  const [unpaidAsOfMonth, setUnpaidAsOfMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });

  // Keep the selection valid as the SY/Semester filter changes — fall back
  // to the latest available month rather than pointing at one that no
  // longer exists in this scope.
  useEffect(() => {
    if (unpaidMonthOptions.includes(unpaidAsOfMonth)) return;
    setUnpaidAsOfMonth(unpaidMonthOptions[unpaidMonthOptions.length - 1] || unpaidAsOfMonth);
  }, [unpaidMonthOptions, unpaidAsOfMonth]);

  // Last millisecond of the selected month — invoices due on or before
  // this are "unpaid as of" that point in time.
  const unpaidAsOfMonthEnd = useMemo(() => {
    const [y, m] = unpaidAsOfMonth.split("-").map(Number);
    if (!y || !m) return Date.now();
    return new Date(y, m, 1).getTime() - 1;
  }, [unpaidAsOfMonth]);

  // Accumulated unpaid balance (every UNPAID/PARTIAL scoped invoice due on
  // or before the selected month, per the picker above) grouped by the
  // student's current grade level. Mirrors the `outstanding` KPI's
  // per-invoice-floor method so a fully-paid student never appears here
  // and an overpaid invoice never offsets another student's balance.
  const unpaidByGrade = useMemo(() => {
    if (!showFinance || !financeKPIs) return { total: 0, rows: [] };
    const paidByInvoice = financeKPIs.paidByInvoice;
    const secMap   = new Map(sections.map(x => [String(x.id), x]));
    const gradeMap = new Map(gradeLevels.map(x => [String(x.id), x]));
    const stuMap   = new Map(students.map(x => [String(x.id), x]));

    const byStudent = new Map(); // studentId -> { student, gradeId, gradeLabel, remaining, invoices }
    for (const inv of scopedInvoices) {
      const st = (inv.status || "").toUpperCase();
      if (st !== "UNPAID" && st !== "PARTIAL") continue;
      const dueMs = inv.dueDate ? new Date(inv.dueDate).getTime() : null;
      if (dueMs != null && dueMs > unpaidAsOfMonthEnd) continue;
      const remaining = Number(inv.amount || 0) - (paidByInvoice.get(inv.id) || 0);
      if (remaining <= 0.005) continue;

      const stu   = stuMap.get(String(inv.studentId));
      const sec   = stu?.currentSectionId ? secMap.get(String(stu.currentSectionId)) : null;
      const grade = sec?.gradeLevelId ? gradeMap.get(String(sec.gradeLevelId)) : null;

      const key = String(inv.studentId);
      let row = byStudent.get(key);
      if (!row) {
        row = {
          studentId: inv.studentId, student: stu || null,
          gradeId: grade?.id ?? "unclassified", gradeLabel: grade?.name ?? "Unclassified",
          remaining: 0, invoices: [],
        };
        byStudent.set(key, row);
      }
      row.remaining += remaining;
      row.invoices.push({
        id: inv.id, amount: Number(inv.amount || 0),
        paid: paidByInvoice.get(inv.id) || 0, remaining,
        dueDate: inv.dueDate, status: inv.status,
      });
    }

    const byGrade = new Map();
    let total = 0;
    for (const row of byStudent.values()) {
      total += row.remaining;
      let g = byGrade.get(row.gradeId);
      if (!g) {
        g = { gradeId: row.gradeId, label: row.gradeLabel, studentCount: 0, amount: 0, students: [] };
        byGrade.set(row.gradeId, g);
      }
      g.studentCount += 1;
      g.amount += row.remaining;
      g.students.push(row);
    }

    const rows = [...byGrade.values()]
      .map(g => ({ ...g, students: g.students.sort((a, b) => b.remaining - a.remaining) }))
      .sort((a, b) => b.amount - a.amount);

    return { total, rows };
  }, [showFinance, financeKPIs, scopedInvoices, sections, gradeLevels, students, unpaidAsOfMonthEnd]);

  const [unpaidGrade,   setUnpaidGrade]   = useState(null); // selected grade row from unpaidByGrade.rows
  const [unpaidStudent, setUnpaidStudent] = useState(null); // student object for the profile modal

  // Daily collection buckets for the last 30 days, scoped to SY/Sem.
  const dailyCollections = useMemo(() => {
    if (!showFinance) return [];
    const buckets = [];
    const start = new Date(thirtyDaysAgo);
    start.setHours(0, 0, 0, 0);
    for (let i = 0; i < 30; i++) {
      const d = new Date(start); d.setDate(start.getDate() + i);
      buckets.push({
        dayStartMs: d.getTime(),
        label: d.toLocaleDateString(undefined, { month: "short", day: "numeric" }),
        amount: 0,
      });
    }
    for (const p of scopedPayments) {
      const t = p.paidAt ? new Date(p.paidAt).getTime() : 0;
      if (!t) continue;
      const d = new Date(t); d.setHours(0, 0, 0, 0);
      const idx = Math.round((d.getTime() - start.getTime()) / (24 * 60 * 60 * 1000));
      if (idx >= 0 && idx < buckets.length) {
        buckets[idx].amount += Number(p.amount || 0);
      }
    }
    return buckets;
  }, [showFinance, scopedPayments, thirtyDaysAgo]);

  // Recent activity — newest 10 events across scoped payments + scoped
  // enrollments, so the feed reflects what's happening inside the SY/Sem
  // the admin picked rather than the whole system.
  const recentActivity = useMemo(() => {
    if (!showFinance) return [];
    const events = [];
    for (const p of scopedPayments) {
      const t = p.paidAt ? new Date(p.paidAt).getTime() : 0;
      if (t) events.push({ kind: "payment", at: t, data: p });
    }
    for (const e of scopedEnrollments) {
      const t = e.enrolledAt ? new Date(e.enrolledAt).getTime() : 0;
      if (t) events.push({ kind: "enrollment", at: t, data: e });
    }
    events.sort((a, b) => b.at - a.at);
    return events.slice(0, 10);
  }, [showFinance, scopedPayments, scopedEnrollments]);

  const activeSy  = schoolYears.find(y => String(y.id) === syId);
  const activeSem = semesters.find(x => String(x.id) === semId);

  const [drilldown,    setDrilldown]    = useState(null); // "students" | "teachers" | ...
  const [showCompare,  setShowCompare]  = useState(false);

  const closeDrill = () => setDrilldown(null);

  const navigate        = useNavigate();
  const showMasterList  = canSeeMasterList();
  function goToMasterList() {
    closeDrill();
    try { sessionStorage.setItem("directory.deeplink", JSON.stringify({ tab: "master-list" })); }
    catch { /* sessionStorage unavailable */ }
    navigate("/directory");
  }

  return (
    <div className={s.wrap}>
      {/* Welcome header */}
      <div className={s.header}>
        <div className={s.eyebrow}>
          {user?.role === "AcademicHead" ? "ACADEMIC HEAD · DASHBOARD" : "ADMIN · DASHBOARD"}
        </div>
        <h1 className={s.title}>
          Welcome{(user?.linkedEntity?.displayName || user?.username)
            ? `, ${user.linkedEntity?.displayName || user.username}` : ""}.
        </h1>
        <p className={s.sub}>
          Live view of the school. See who teaches what, spot unassigned teachers
          and uncovered subjects, and drill into Academics to fix gaps.
        </p>
      </div>

      {/* Filter */}
      <div className={s.filterCard}>
        <label className={s.filterField}>
          <span>School Year</span>
          <select value={syId} onChange={e => setSyId(e.target.value)}>
            {schoolYears.map(y => (
              <option key={y.id} value={y.id}>
                {y.label}{y.active ? " · Active" : ""}
              </option>
            ))}
          </select>
        </label>
        <label className={s.filterField}>
          <span>Semester</span>
          <select value={semId} onChange={e => setSemId(e.target.value)}>
            {semesters
              .filter(x => !syId || String(x.schoolYearId) === String(syId))
              .map(x => (
                <option key={x.id} value={x.id}>
                  {x.label}{x.active ? " · Active" : ""}
                </option>
              ))}
          </select>
        </label>
        <div className={s.filterActive}>
          {activeSy  && <span className={s.activePill}>{activeSy.label}</span>}
          {activeSem && <span className={s.activePill}>{activeSem.label}</span>}
        </div>
      </div>

      {/* Stat tiles */}
      <div className={s.statGrid}>
        <StatTile label="Students"        value={loading ? "…" : enrolledStudents}
                  sub={loading ? "" : `${students.length} total on file`}
                  onClick={() => setDrilldown("students")} />
        <StatTile label="Teachers"        value={loading ? "…" : teachers.length}
                  sub={loading ? "" : `${teachers.length - teachersUnassigned} assigned`}
                  onClick={() => setDrilldown("teachers")} />
        <StatTile label="Sections"        value={loading ? "…" : sectionsInSY.length}
                  sub={loading ? "" : `for ${activeSy?.label || "—"}`}
                  onClick={() => setDrilldown("sections")} />
        <StatTile label="Subjects"        value={loading ? "…" : subjects.length}
                  sub={loading ? "" : `${subjectsUncovered} not offered yet`}
                  onClick={() => setDrilldown("subjects")} />
        <StatTile label="Class Offerings" value={loading ? "…" : offeringsInSem.length}
                  sub={loading ? "" : `this semester`}
                  onClick={() => setDrilldown("offerings")} />
        <StatTile label="Gaps"            value={loading ? "…" : gapCount}
                  sub={loading ? "" : "teachers + subjects"}
                  tone={gapCount > 0 ? "warn" : "ok"}
                  onClick={() => setDrilldown("gaps")} />
      </div>

      {drilldown && (
        <DrilldownModal
          kind={drilldown}
          onClose={closeDrill}
          onMasterList={showMasterList ? goToMasterList : null}
          ctx={{
            students, teachers, sections: sectionsInSY, subjects,
            offerings: offeringsInSem,
            sectionById, subjectById, employeeById,
            offeringsByTeacher, offeringsBySubject,
            activeSy, activeSem,
            teachersSorted, subjectsSorted,
            gradeLevelById, sectionFullLabel,
            user,
          }}
        />
      )}

      {/* Financial Overview — premium 6-metric summary card (Admin/SuperAdmin only).
          Sits above the sparkline snapshot so the headline numbers get seen
          before the time-series detail. */}
      {showFinance && financeKPIs && (
        <FinancialOverview
          loading={loading}
          kpis={financeKPIs}
          activeSy={activeSy}
          activeSem={activeSem}
          s={s}
          onCompare={() => setShowCompare(true)}
          rawData={financeRawData}
        />
      )}

      {showCompare && (
        <FinancialCompareModal
          onClose={() => setShowCompare(false)}
          payments={scopedPayments}
          invoices={scopedInvoices}
          students={students}
          activeSy={activeSy}
          activeSem={activeSem}
        />
      )}

      {/* Finance snapshot — Admin/SuperAdmin only. Lives between the academic
          stat tiles (where it's most likely to get a glance) and the two
          coverage panels (which push the finance block further down if it
          went last). */}
      {showFinance && financeKPIs && (
        <FinanceSnapshot
          loading={loading}
          kpis={financeKPIs}
          daily={dailyCollections}
          activity={recentActivity}
          studentById={Object.fromEntries(students.map(x => [x.id, x]))}
          sectionById={sectionById}
          sectionFullLabel={sectionFullLabel}
          invoiceById={Object.fromEntries(invoices.map(x => [x.id, x]))}
          activeSy={activeSy}
          activeSem={activeSem}
          s={s}
          rawData={financeRawData}
          unpaidByGrade={unpaidByGrade}
          unpaidMonthOptions={unpaidMonthOptions}
          unpaidAsOfMonth={unpaidAsOfMonth}
          onUnpaidMonthChange={setUnpaidAsOfMonth}
          onSelectUnpaidGrade={setUnpaidGrade}
        />
      )}

      {unpaidGrade && (
        <UnpaidGradeModal
          grade={unpaidGrade}
          monthLabel={formatMonthKey(unpaidAsOfMonth)}
          onClose={() => setUnpaidGrade(null)}
          onSelectStudent={setUnpaidStudent}
          s={s}
        />
      )}

      {unpaidStudent && (
        <StudentProfileModal
          open={!!unpaidStudent}
          student={unpaidStudent}
          sections={sections}
          gradeLevels={gradeLevels}
          schoolYears={schoolYears}
          semesters={semesters}
          onClose={() => setUnpaidStudent(null)}
          zIndex={1200}
        />
      )}

      {/* Two coverage panels */}
      <div className={s.panelGrid}>
        {/* Teaching Load */}
        <section className={s.panel}>
          <header className={s.panelHead}>
            <div>
              <h2 className={s.panelTitle}>Teaching Load</h2>
              <p className={s.panelSub}>
                {teachers.length === 0
                  ? "No teaching staff yet. Add one under Staff."
                  : teachersUnassigned > 0
                    ? `${teachersUnassigned} teacher${teachersUnassigned === 1 ? "" : "s"} have no classes this semester.`
                    : "Every teacher has at least one class. Nice."}
              </p>
            </div>
            <a className={s.panelAction} href="/academics">Open Academics →</a>
          </header>

          {loading ? (
            <div className={s.empty}>Loading…</div>
          ) : teachers.length === 0 ? (
            <div className={s.empty}>No teachers found.</div>
          ) : (
            <ul className={s.list}>
              {teachersSorted.map(t => {
                const load = offeringsByTeacher.get(t.id) || [];
                return (
                  <li key={t.id} className={`${s.row} ${load.length === 0 ? s.rowWarn : ""}`}>
                    {t.photoUrl
                      ? <img src={t.photoUrl} alt="" className={s.avatarImg} />
                      : <div className={s.avatar} style={{ background: avatarColor(t.name) }}>
                          {initials(t.name)}
                        </div>
                    }
                    <div className={s.rowBody}>
                      <div className={s.rowName}>{t.name}</div>
                      <div className={s.rowMeta}>
                        {t.role || "Teacher"}
                        {load.length === 0
                          ? <span className={s.badgeWarn}>Unassigned</span>
                          : <span className={s.badgeOk}>{load.length} class{load.length === 1 ? "" : "es"}</span>
                        }
                      </div>
                      {load.length > 0 && (
                        <div className={s.pillRow}>
                          {load.slice(0, 6).map(o => {
                            const subj = subjectById[o.subjectId];
                            const sec  = sectionById[o.sectionId];
                            return (
                              <span key={o.id} className={s.pill}>
                                <span className={s.pillCode}>{subj?.code || "?"}</span>
                                <span className={s.pillSep}>·</span>
                                <span>{sec?.name || "?"}</span>
                              </span>
                            );
                          })}
                          {load.length > 6 && (
                            <span className={s.pillMore}>+{load.length - 6} more</span>
                          )}
                        </div>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        {/* Subject Coverage */}
        <section className={s.panel}>
          <header className={s.panelHead}>
            <div>
              <h2 className={s.panelTitle}>Subject Coverage</h2>
              <p className={s.panelSub}>
                {subjects.length === 0
                  ? "No subjects yet. Add them under Academics → Subjects."
                  : subjectsUncovered > 0
                    ? `${subjectsUncovered} subject${subjectsUncovered === 1 ? "" : "s"} not offered yet this semester.`
                    : "Every subject is offered in at least one section."}
              </p>
            </div>
            <a className={s.panelAction} href="/academics">Open Academics →</a>
          </header>

          {loading ? (
            <div className={s.empty}>Loading…</div>
          ) : subjects.length === 0 ? (
            <div className={s.empty}>No subjects found.</div>
          ) : (
            <ul className={s.list}>
              {subjectsSorted.map(x => {
                const offs = offeringsBySubject.get(x.id) || [];
                return (
                  <li key={x.id} className={`${s.row} ${offs.length === 0 ? s.rowWarn : ""}`}>
                    <div className={s.subjPill}>{x.code}</div>
                    <div className={s.rowBody}>
                      <div className={s.rowName}>{x.title}</div>
                      <div className={s.rowMeta}>
                        {x.units != null && <span>{x.units} units</span>}
                        {offs.length === 0
                          ? <span className={s.badgeWarn}>Not offered</span>
                          : <span className={s.badgeOk}>{offs.length} offering{offs.length === 1 ? "" : "s"}</span>
                        }
                      </div>
                      {offs.length > 0 && (
                        <div className={s.pillRow}>
                          {offs.slice(0, 6).map(o => {
                            const sec     = sectionById[o.sectionId];
                            const teacher = employeeById[o.teacherStaffId];
                            return (
                              <span key={o.id} className={s.pill}>
                                <span>{sec?.name || "?"}</span>
                                <span className={s.pillSep}>·</span>
                                <span className={s.pillTeacher}>
                                  {teacher?.name?.split(" ").slice(-1)[0] || "—"}
                                </span>
                              </span>
                            );
                          })}
                          {offs.length > 6 && (
                            <span className={s.pillMore}>+{offs.length - 6} more</span>
                          )}
                        </div>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      </div>

      <RecentAnnouncementsWidget />
    </div>
  );
}

/** Peso formatter — the figures on this dashboard are always PHP and the
 *  thousands separator matters for readability. */
function peso(n) {
  const v = Number(n || 0);
  return "₱" + v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** "2026-09" -> "September 2026". Used by the Unpaid by Grade Level picker. */
function formatMonthKey(key) {
  const [y, m] = (key || "").split("-").map(Number);
  if (!y || !m) return "—";
  return new Date(y, m - 1, 1).toLocaleDateString(undefined, { month: "long", year: "numeric" });
}

function buildFovDetail(tileKey, rawData, kpis) {
  if (!rawData) return null;
  const { invoices = [], payments = [], allPayments = [],
          expenses = [], feeStructures = [], sections = [], students = [], gradeLevels = [],
          monthStart = null, monthEnd = null } = rawData;

  const stuMap  = new Map(students.map(x => [String(x.id), x]));
  const secMap  = new Map(sections.map(x => [String(x.id), x]));
  const gradeMap = new Map(gradeLevels.map(x => [String(x.id), x]));
  const invMap  = new Map(invoices.map(x => [String(x.id), x]));
  const feeMap  = new Map();
  for (const fs of feeStructures) feeMap.set(`${fs.gradeLevelId}|${fs.schoolYearId}`, Number(fs.totalAmount || 0));

  const fmtDate = iso => {
    if (!iso) return "—";
    const d = new Date(iso);
    return isNaN(d) ? "—" : d.toLocaleDateString("en-PH", { year: "numeric", month: "short", day: "numeric" });
  };

  const stuName = stu => stu ? `${stu.firstName || ""} ${stu.lastName || ""}`.trim() || `#${stu.id}` : "—";
  const glOf = inv => {
    const stu = stuMap.get(String(inv.studentId));
    const sec = stu?.currentSectionId ? secMap.get(String(stu.currentSectionId)) : null;
    return sec?.gradeLevelId ? gradeMap.get(String(sec.gradeLevelId)) : null;
  };
  const feeOf = inv => {
    const stu = stuMap.get(String(inv.studentId));
    const sec = stu?.currentSectionId ? secMap.get(String(stu.currentSectionId)) : null;
    const key = sec?.gradeLevelId && inv.schoolYearId ? `${sec.gradeLevelId}|${inv.schoolYearId}` : null;
    return key ? feeMap.get(key) : null;
  };

  switch (tileKey) {
    case "grand": {
      const byGrade = new Map();
      for (const inv of invoices) {
        const gl    = glOf(inv);
        const fsAmt = feeOf(inv) ?? Number(inv.amount || 0);
        const id    = gl?.id ?? "unk";
        const lbl   = gl?.name ?? "Unclassified";
        if (!byGrade.has(id)) byGrade.set(id, { label: lbl, feeAmt: fsAmt, count: 0, subtotal: 0 });
        const r = byGrade.get(id); r.count++; r.subtotal += fsAmt; r.feeAmt = fsAmt;
      }
      return { type: "grand", rows: [...byGrade.values()].sort((a,b) => a.label.localeCompare(b.label)) };
    }
    case "gross": {
      const byGrade = new Map();
      for (const inv of invoices) {
        const gl    = glOf(inv);
        const fsAmt = feeOf(inv) ?? Number(inv.amount || 0);
        const invAmt = Number(inv.amount || 0);
        const id    = gl?.id ?? "unk";
        const lbl   = gl?.name ?? "Unclassified";
        if (!byGrade.has(id)) byGrade.set(id, { label: lbl, count: 0, gross: 0, grand: 0 });
        const r = byGrade.get(id); r.count++; r.gross += invAmt; r.grand += fsAmt;
      }
      return {
        type: "gross",
        rows: [...byGrade.values()]
          .map(r => ({ ...r, discount: r.grand - r.gross }))
          .sort((a,b) => a.label.localeCompare(b.label)),
      };
    }
    case "pay": {
      const effDate = p => p.paymentReceivedOn
        ? new Date(p.paymentReceivedOn).getTime()
        : new Date(p.paidAt || 0).getTime();
      const effLabel = p => {
        if (p.paymentReceivedOn) return fmtDate(p.paymentReceivedOn);
        return p.paidAt ? fmtDate(p.paidAt) : "—";
      };
      const rows = [...payments]
        .sort((a,b) => effDate(b) - effDate(a))
        .map(p => {
          const inv = invMap.get(String(p.invoiceId));
          const stu = inv ? stuMap.get(String(inv.studentId)) : null;
          return { date: effLabel(p), dateTs: effDate(p), student: stuName(stu), amount: Number(p.amount||0), method: p.method||"—" };
        });
      return {
        type: "pay", rows: rows.slice(0,100), allRows: rows, total: rows.length,
        sectionLabel: `Payment Records ${rows.length > 100 ? `(showing 100 of ${rows.length})` : `(${rows.length} total)`}`,
        footerLabel: "TOTAL PAYMENTS",
        footerTotal: kpis?.totalPayments ?? 0,
      };
    }
    case "month": {
      // Scoped to the same calendar month as `thisMonthCollected` — BOTH
      // the membership filter and the displayed date now key off the
      // actual RECEIVED date (paymentReceivedOn, falling back to paidAt
      // for legacy rows with none recorded), bounded on both ends of the
      // month. A payment entered this month but backdated to an earlier
      // month must not count as "collected this month", and conversely
      // one entered last month but received this month should show up
      // here — "this month" means the money actually arrived this month,
      // not that a cashier happened to type it in during this month.
      const effDate  = p => p.paymentReceivedOn
        ? new Date(p.paymentReceivedOn).getTime()
        : new Date(p.paidAt || 0).getTime();
      const effLabel = p => p.paymentReceivedOn ? fmtDate(p.paymentReceivedOn) : (p.paidAt ? fmtDate(p.paidAt) : "—");
      const rows = payments
        .filter(p => {
          const t = effDate(p);
          return monthStart != null && monthEnd != null && t >= monthStart && t <= monthEnd;
        })
        .sort((a,b) => effDate(b) - effDate(a))
        .map(p => {
          const inv = invMap.get(String(p.invoiceId));
          const stu = inv ? stuMap.get(String(inv.studentId)) : null;
          return { date: effLabel(p), dateTs: effDate(p), student: stuName(stu), amount: Number(p.amount||0), method: p.method||"—" };
        });
      return {
        type: "pay", rows: rows.slice(0,100), allRows: rows, total: rows.length,
        sectionLabel: `This Month's Payments ${rows.length > 100 ? `(showing 100 of ${rows.length})` : `(${rows.length} total)`}`,
        footerLabel: "TOTAL COLLECTED THIS MONTH",
        footerTotal: kpis?.thisMonthCollected ?? rows.reduce((sum,r) => sum + r.amount, 0),
      };
    }
    case "exp": {
      // Every manually-recorded, non-voided expense as its own line —
      // not an aggregate, so the drill-down actually shows what the
      // money went to (not just a category total). Payroll is
      // intentionally absent here: it's no longer auto-blended into
      // Total Expenses — see the owner-control note on kpis.totalExpenses.
      const rows = expenses
        .filter(x => x.status !== "VOIDED")
        .map(x => ({
          date: fmtDate(x.expenseDate),
          dateTs: x.expenseDate ? new Date(x.expenseDate).getTime() : 0,
          category: x.category || "Miscellaneous",
          description: x.description || "—",
          payee: x.payee || "—",
          amount: Number(x.amount || 0),
          // Carried through (not shown as table columns) for the row-click
          // detail popup — full Notes text in particular doesn't fit inline.
          notes: x.notes || "",
          paymentMethod: x.paymentMethod || "",
          referenceNo: x.referenceNo || "",
          addedByUsername: x.addedByUsername || "",
          addedAt: x.addedAt || null,
        }))
        .sort((a,b) => b.dateTs - a.dateTs);
      return { type: "exp", rows };
    }
    case "dep": case "dep_neg": {
      return { type: "dep",
        totalPayments:  kpis?.totalPayments  ?? 0,
        totalExpenses:  kpis?.totalExpenses  ?? 0,
        currentDeposits: kpis?.currentDeposits ?? 0 };
    }
    case "col": {
      const paidByInv = new Map();
      for (const p of allPayments) paidByInv.set(String(p.invoiceId), (paidByInv.get(String(p.invoiceId))||0) + Number(p.amount||0));
      const byGrade = new Map();
      for (const inv of invoices) {
        const invAmt    = Number(inv.amount||0);
        const paid      = paidByInv.get(String(inv.id))||0;
        const remaining = Math.max(0, invAmt - paid);
        if (remaining < 0.005) continue;
        const gl  = glOf(inv);
        const id  = gl?.id ?? "unk";
        const lbl = gl?.name ?? "Unclassified";
        if (!byGrade.has(id)) byGrade.set(id, { label: lbl, count: 0, gross: 0, paid: 0, remaining: 0 });
        const r = byGrade.get(id); r.count++; r.gross += invAmt; r.paid += paid; r.remaining += remaining;
      }
      return { type: "col", rows: [...byGrade.values()].sort((a,b) => b.remaining - a.remaining) };
    }
    default: return null;
  }
}

/** Default { key, dir } a breakdown table opens sorted by — mirrors the
 *  order buildFovDetail already produces so the first render looks
 *  unchanged; clicking a column header re-sorts from there. */
function defaultSortFor(type) {
  switch (type) {
    case "grand":
    case "gross":  return { key: "label", dir: "asc" };
    case "col":    return { key: "remaining", dir: "desc" };
    case "pay":    return { key: "dateTs", dir: "desc" };
    case "exp":    return { key: "dateTs", dir: "desc" };
    default:       return { key: null, dir: "asc" };
  }
}

function sortRows(rows, sort) {
  if (!sort?.key) return rows;
  const { key, dir } = sort;
  const copy = [...rows];
  copy.sort((a, b) => {
    const av = a[key], bv = b[key];
    let cmp;
    if (typeof av === "string" || typeof bv === "string") {
      cmp = String(av ?? "").localeCompare(String(bv ?? ""), undefined, { sensitivity: "base" });
    } else {
      cmp = (Number(av) || 0) - (Number(bv) || 0);
    }
    return dir === "asc" ? cmp : -cmp;
  });
  return copy;
}

/** Clickable <th> that toggles sort on a breakdown table. Clicking a new
 *  column starts at `defaultDir`; clicking the active column flips it. */
function SortTh({ children, sortKey, sort, onSort, defaultDir = "asc", right }) {
  const active = sort.key === sortKey;
  return (
    <th
      className={`${s.fovModalTh} ${right ? s.fovModalThR : ""} ${s.fovModalThSort} ${active ? s.fovModalThSortActive : ""}`}
      onClick={() => onSort(sortKey, defaultDir)}
      aria-sort={active ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}
    >
      <span className={s.fovModalThInner}>
        {children}
        <svg className={s.fovModalSortIcon} width="9" height="9" viewBox="0 0 9 9" fill="currentColor" aria-hidden="true">
          {active && sort.dir === "asc"
            ? <path d="M4.5 1.5L8 6.5H1L4.5 1.5Z"/>
            : <path d="M4.5 7.5L1 2.5H8L4.5 7.5Z"/>}
        </svg>
      </span>
    </th>
  );
}

function fmtDateTimeShort(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d)) return null;
  return d.toLocaleString("en-PH", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
}

/** Read-only detail popup for one expense row in the "Total Expenses"
 *  drill-down — surfaces the full Description and Notes text, neither of
 *  which fit in the table row. Stacks above FovInfoModal (its own
 *  createPortal + higher z-index), same pattern StudentProfileModal uses
 *  to stack above other dashboard modals. */
function ExpenseDetailPopup({ expense, onClose }) {
  useEffect(() => {
    const fn = e => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", fn);
    return () => document.removeEventListener("keydown", fn);
  }, [onClose]);

  return createPortal(
    <div className={s.fovDetailOverlay} role="dialog" aria-modal="true" aria-label="Expense details">
      <div className={s.fovDetailModal} onClick={e => e.stopPropagation()}>
        <div className={s.fovDetailHead}>
          <h3 className={s.fovDetailTitle}>Expense Details</h3>
          <button className={s.fovModalClose} onClick={onClose} aria-label="Close">
            <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="1" y1="1" x2="12" y2="12"/><line x1="12" y1="1" x2="1" y2="12"/>
            </svg>
          </button>
        </div>
        <div className={s.fovDetailBody}>
          <div className={s.fovDetailTop}>
            <span className={s.fovModalBadge}>{expense.category}</span>
            <span className={s.fovDetailAmount}>{peso(expense.amount)}</span>
          </div>
          <div className={s.fovDetailSection}>
            <div className={s.fovDetailLabel}>Description</div>
            <div className={s.fovDetailValue}>{expense.description || "—"}</div>
          </div>
          <div className={s.fovDetailGrid}>
            <div>
              <div className={s.fovDetailLabel}>Date</div>
              <div className={s.fovDetailValue}>{expense.date}</div>
            </div>
            <div>
              <div className={s.fovDetailLabel}>Payment method</div>
              <div className={s.fovDetailValue}>{expense.paymentMethod || "—"}</div>
            </div>
            <div>
              <div className={s.fovDetailLabel}>Paid to</div>
              <div className={s.fovDetailValue}>{expense.payee}</div>
            </div>
            <div>
              <div className={s.fovDetailLabel}>Reference #</div>
              <div className={s.fovDetailValue}>{expense.referenceNo || "—"}</div>
            </div>
          </div>
          <div className={s.fovDetailSection}>
            <div className={s.fovDetailLabel}>Notes</div>
            <div className={`${s.fovDetailValue} ${!expense.notes ? s.fovDetailValueMuted : ""}`}>
              {expense.notes || "No notes added."}
            </div>
          </div>
          {expense.addedByUsername && (
            <div className={s.fovDetailAudit}>
              Recorded by {expense.addedByUsername}
              {fmtDateTimeShort(expense.addedAt) ? ` · ${fmtDateTimeShort(expense.addedAt)}` : ""}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

function FovInfoModal({ tile, onClose, rawData, kpis, scopeLabel }) {
  const infoKey = tile.key.replace("_neg","");
  const detail  = buildFovDetail(tile.key, rawData, kpis);

  const [sort, setSort] = useState(() => defaultSortFor(detail?.type));
  // Selected row for the "exp" table's click-through detail popup — the
  // only type with columns that don't fit inline (full Description text
  // was already visible, but Notes never had anywhere to go).
  const [expDetail, setExpDetail] = useState(null);
  useEffect(() => {
    setSort(defaultSortFor(detail?.type));
    setExpDetail(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tile.key]);
  const onSort = (key, defaultDir) => setSort(prev =>
    prev.key === key ? { key, dir: prev.dir === "asc" ? "desc" : "asc" } : { key, dir: defaultDir });

  const sortedRows = useMemo(() => sortRows(detail?.rows || [], sort), [detail, sort]);

  useEffect(() => {
    const fn = e => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", fn);
    return () => document.removeEventListener("keydown", fn);
  }, [onClose]);

  const peso = v => `₱${Number(v||0).toLocaleString("en-PH",{minimumFractionDigits:2,maximumFractionDigits:2})}`;

  function DetailContent() {
    if (!detail) return null;
    if (detail.type === "grand") return (
      <>
        <div className={s.fovModalSectionLabel}>Breakdown by Grade Level</div>
        <div className={s.fovModalTableWrap}>
          <table className={s.fovModalTable}>
            <thead><tr>
              <SortTh sortKey="label" sort={sort} onSort={onSort} defaultDir="asc">Grade Level</SortTh>
              <SortTh sortKey="feeAmt" sort={sort} onSort={onSort} defaultDir="desc" right>Fee Structure</SortTh>
              <SortTh sortKey="count" sort={sort} onSort={onSort} defaultDir="desc" right>Students</SortTh>
              <SortTh sortKey="subtotal" sort={sort} onSort={onSort} defaultDir="desc" right>Subtotal</SortTh>
            </tr></thead>
            <tbody>
              {sortedRows.map((r,i) => (
                <tr key={i} className={s.fovModalTr}>
                  <td className={s.fovModalTd}>{r.label}</td>
                  <td className={`${s.fovModalTd} ${s.fovModalTdR}`}>{peso(r.feeAmt)}</td>
                  <td className={`${s.fovModalTd} ${s.fovModalTdR}`}>{r.count}</td>
                  <td className={`${s.fovModalTd} ${s.fovModalTdR} ${s.fovModalTdAccent}`}>{peso(r.subtotal)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot><tr className={s.fovModalTrTotal}>
              <td className={s.fovModalTd} colSpan={2}>TOTAL</td>
              <td className={`${s.fovModalTd} ${s.fovModalTdR}`}>{detail.rows.reduce((s,r)=>s+r.count,0)}</td>
              <td className={`${s.fovModalTd} ${s.fovModalTdR} ${s.fovModalTdAccent}`}>{peso(kpis?.grandTotal??0)}</td>
            </tr></tfoot>
          </table>
        </div>
      </>
    );
    if (detail.type === "gross") return (
      <>
        <div className={s.fovModalSectionLabel}>Breakdown by Grade Level</div>
        <div className={s.fovModalTableWrap}>
          <table className={s.fovModalTable}>
            <thead><tr>
              <SortTh sortKey="label" sort={sort} onSort={onSort} defaultDir="asc">Grade Level</SortTh>
              <SortTh sortKey="count" sort={sort} onSort={onSort} defaultDir="desc" right>Students</SortTh>
              <SortTh sortKey="gross" sort={sort} onSort={onSort} defaultDir="desc" right>Gross Total</SortTh>
              <SortTh sortKey="discount" sort={sort} onSort={onSort} defaultDir="desc" right>Discounts</SortTh>
            </tr></thead>
            <tbody>
              {sortedRows.map((r,i) => (
                <tr key={i} className={s.fovModalTr}>
                  <td className={s.fovModalTd}>{r.label}</td>
                  <td className={`${s.fovModalTd} ${s.fovModalTdR}`}>{r.count}</td>
                  <td className={`${s.fovModalTd} ${s.fovModalTdR} ${s.fovModalTdAccent}`}>{peso(r.gross)}</td>
                  <td className={`${s.fovModalTd} ${s.fovModalTdR} ${s.fovModalTdDiscount}`}>{r.discount > 0.005 ? `−${peso(r.discount)}` : "—"}</td>
                </tr>
              ))}
            </tbody>
            <tfoot><tr className={s.fovModalTrTotal}>
              <td className={s.fovModalTd}>TOTAL</td>
              <td className={`${s.fovModalTd} ${s.fovModalTdR}`}>{detail.rows.reduce((s,r)=>s+r.count,0)}</td>
              <td className={`${s.fovModalTd} ${s.fovModalTdR} ${s.fovModalTdAccent}`}>{peso(kpis?.grossTotal??0)}</td>
              <td className={`${s.fovModalTd} ${s.fovModalTdR} ${s.fovModalTdDiscount}`}>{kpis && kpis.grandTotal > kpis.grossTotal ? `−${peso(kpis.grandTotal - kpis.grossTotal)}` : "—"}</td>
            </tr></tfoot>
          </table>
        </div>
      </>
    );
    if (detail.type === "pay") return (
      <>
        <div className={s.fovModalSectionLabel}>{detail.sectionLabel}</div>
        {detail.rows.length === 0 ? (
          <div className={s.fovModalEmpty}>No payments in this period.</div>
        ) : (
          <div className={s.fovModalTableWrap}>
            <table className={s.fovModalTable}>
              <thead><tr>
                <SortTh sortKey="dateTs" sort={sort} onSort={onSort} defaultDir="desc">Date</SortTh>
                <SortTh sortKey="student" sort={sort} onSort={onSort} defaultDir="asc">Student</SortTh>
                <SortTh sortKey="method" sort={sort} onSort={onSort} defaultDir="asc" right>Method</SortTh>
                <SortTh sortKey="amount" sort={sort} onSort={onSort} defaultDir="desc" right>Amount</SortTh>
              </tr></thead>
              <tbody>
                {sortedRows.map((r,i) => (
                  <tr key={i} className={s.fovModalTr}>
                    <td className={`${s.fovModalTd} ${s.fovModalTdDate}`}>{r.date}</td>
                    <td className={s.fovModalTd}>{r.student}</td>
                    <td className={`${s.fovModalTd} ${s.fovModalTdR}`}><span className={s.fovModalBadge}>{r.method}</span></td>
                    <td className={`${s.fovModalTd} ${s.fovModalTdR} ${s.fovModalTdAccent}`}>{peso(r.amount)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot><tr className={s.fovModalTrTotal}>
                <td className={s.fovModalTd} colSpan={3}>{detail.footerLabel}</td>
                <td className={`${s.fovModalTd} ${s.fovModalTdR} ${s.fovModalTdAccent}`}>{peso(detail.footerTotal)}</td>
              </tr></tfoot>
            </table>
          </div>
        )}
      </>
    );
    if (detail.type === "exp") return (
      <>
        <div className={s.fovModalSectionLabel}>Recorded Expenses ({detail.rows.length})</div>
        {detail.rows.length === 0 ? (
          <div className={s.fovModalEmpty}>
            No expenses recorded yet. Add one from the Expenses page.
          </div>
        ) : (
          <div className={s.fovModalTableWrap}>
            <table className={s.fovModalTable}>
              <thead><tr>
                <SortTh sortKey="dateTs" sort={sort} onSort={onSort} defaultDir="desc">Date</SortTh>
                <SortTh sortKey="category" sort={sort} onSort={onSort} defaultDir="asc">Category</SortTh>
                <SortTh sortKey="description" sort={sort} onSort={onSort} defaultDir="asc">Description</SortTh>
                <SortTh sortKey="payee" sort={sort} onSort={onSort} defaultDir="asc">Paid To</SortTh>
                <SortTh sortKey="amount" sort={sort} onSort={onSort} defaultDir="desc" right>Amount</SortTh>
              </tr></thead>
              <tbody>
                {sortedRows.map((r,i) => (
                  <tr
                    key={i}
                    className={`${s.fovModalTr} ${s.fovModalTrClickable}`}
                    onClick={() => setExpDetail(r)}
                    onKeyDown={e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setExpDetail(r); } }}
                    tabIndex={0}
                    role="button"
                    title="Click for full details"
                  >
                    <td className={`${s.fovModalTd} ${s.fovModalTdDate}`}>{r.date}</td>
                    <td className={s.fovModalTd}><span className={s.fovModalBadge}>{r.category}</span></td>
                    <td className={s.fovModalTd}>{r.description}</td>
                    <td className={s.fovModalTd}>{r.payee}</td>
                    <td className={`${s.fovModalTd} ${s.fovModalTdR} ${s.fovModalTdAccent}`}>{peso(r.amount)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot><tr className={s.fovModalTrTotal}>
                <td className={s.fovModalTd} colSpan={4}>TOTAL EXPENSES</td>
                <td className={`${s.fovModalTd} ${s.fovModalTdR} ${s.fovModalTdAccent}`}>{peso(kpis?.totalExpenses??0)}</td>
              </tr></tfoot>
            </table>
          </div>
        )}
      </>
    );
    if (detail.type === "dep") {
      const neg = detail.currentDeposits < -0.005;
      return (
        <>
          <div className={s.fovModalSectionLabel}>Calculation Breakdown</div>
          <div className={s.fovModalDepWrap}>
            <div className={`${s.fovModalDepRow} ${s.fovModalDepPlus}`}>
              <span className={s.fovModalDepLabel}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/>
                </svg>
                Total Payments Collected
              </span>
              <span className={s.fovModalDepValue}>{peso(detail.totalPayments)}</span>
            </div>
            <div className={`${s.fovModalDepRow} ${s.fovModalDepMinus}`}>
              <span className={s.fovModalDepLabel}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                </svg>
                Total Monthly Expenses
              </span>
              <span className={s.fovModalDepValue}>−{peso(detail.totalExpenses)}</span>
            </div>
            <div className={`${s.fovModalDepRow} ${s.fovModalDepTotal} ${neg ? s.fovModalDepNeg : s.fovModalDepPos}`}>
              <span className={s.fovModalDepLabel}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
                </svg>
                {neg ? "Net Deficit" : "Net Surplus"}
              </span>
              <span className={s.fovModalDepValue}>{peso(detail.currentDeposits)}</span>
            </div>
          </div>
        </>
      );
    }
    if (detail.type === "col") return (
      <>
        <div className={s.fovModalSectionLabel}>Outstanding by Grade Level</div>
        <div className={s.fovModalTableWrap}>
          <table className={s.fovModalTable}>
            <thead><tr>
              <SortTh sortKey="label" sort={sort} onSort={onSort} defaultDir="asc">Grade Level</SortTh>
              <SortTh sortKey="count" sort={sort} onSort={onSort} defaultDir="desc" right>Students</SortTh>
              <SortTh sortKey="gross" sort={sort} onSort={onSort} defaultDir="desc" right>Billed</SortTh>
              <SortTh sortKey="paid" sort={sort} onSort={onSort} defaultDir="desc" right>Paid</SortTh>
              <SortTh sortKey="remaining" sort={sort} onSort={onSort} defaultDir="desc" right>Remaining</SortTh>
            </tr></thead>
            <tbody>
              {sortedRows.map((r,i) => (
                <tr key={i} className={s.fovModalTr}>
                  <td className={s.fovModalTd}>{r.label}</td>
                  <td className={`${s.fovModalTd} ${s.fovModalTdR}`}>{r.count}</td>
                  <td className={`${s.fovModalTd} ${s.fovModalTdR}`}>{peso(r.gross)}</td>
                  <td className={`${s.fovModalTd} ${s.fovModalTdR}`}>{peso(r.paid)}</td>
                  <td className={`${s.fovModalTd} ${s.fovModalTdR} ${s.fovModalTdAccent}`}>{peso(r.remaining)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot><tr className={s.fovModalTrTotal}>
              <td className={s.fovModalTd} colSpan={4}>TOTAL REMAINING</td>
              <td className={`${s.fovModalTd} ${s.fovModalTdR} ${s.fovModalTdAccent}`}>{peso(kpis?.remainingCollectibles??0)}</td>
            </tr></tfoot>
          </table>
        </div>
      </>
    );
    return null;
  }

  return (
    <>
      {createPortal(
        <div className={s.fovOverlay} role="dialog" aria-modal="true" aria-label={`${tile.label} details`}>
          <div className={s.fovModal} onClick={e => e.stopPropagation()}>
            <div className={s.fovModalHead}>
              <div className={`${s.fovModalHeadIcon} ${s[`fovModalIcon_${infoKey}`]}`}>{tile.icon}</div>
              <div className={s.fovModalHeadText}>
                <div className={s.fovModalEyebrow}>Financial Metric Detail</div>
                <div className={s.fovModalTitle}>{tile.label}</div>
              </div>
              <button
                type="button"
                className={s.fovModalPdfBtn}
                onClick={() => exportFinanceDetailPdf({
                  title: tile.label,
                  detail,
                  // Payments: every record, not just the 100 on screen.
                  rows: sortRows(detail?.allRows || detail?.rows || [], sort),
                  kpis,
                  scopeLabel,
                })}
                disabled={!detail}
                title="Download this breakdown as a PDF">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                     strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                PDF
              </button>
              <button className={s.fovModalClose} onClick={onClose} aria-label="Close">
                <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <line x1="1" y1="1" x2="12" y2="12"/><line x1="12" y1="1" x2="1" y2="12"/>
                </svg>
              </button>
            </div>
            <div className={s.fovModalBody}>
              <DetailContent />
            </div>
          </div>
        </div>,
        document.body
      )}
      {expDetail && <ExpenseDetailPopup expense={expDetail} onClose={() => setExpDetail(null)} />}
    </>
  );
}

function FinancialOverview({ loading, kpis, activeSy, activeSem, s, rawData, onCompare }) {
  const scopeLabel = [activeSy?.label, activeSem?.label].filter(Boolean).join(" · ");
  const depositsNeg = kpis.currentDeposits < -0.005;
  const [openInfo, setOpenInfo] = useState(null);

  const tiles = [
    {
      key: "grand",
      label: "Grand Total",
      value: kpis.grandTotal,
      desc: "All school fees before discounts",
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
             stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-4 0v2"/>
          <line x1="12" y1="12" x2="12" y2="16"/><line x1="10" y1="14" x2="14" y2="14"/>
        </svg>
      ),
    },
    {
      key: "gross",
      label: "Gross Total",
      value: kpis.grossTotal,
      desc: "After student discounts applied",
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
             stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 2L2 7l10 5 10-5-10-5z"/>
          <path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/>
        </svg>
      ),
    },
    {
      key: "pay",
      label: "Total Payments",
      value: kpis.totalPayments,
      desc: "Collected in this scope",
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
             stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="2" y="5" width="20" height="14" rx="2"/>
          <line x1="2" y1="10" x2="22" y2="10"/>
        </svg>
      ),
    },
    {
      key: "exp",
      label: "Total Expenses",
      value: kpis.totalExpenses,
      desc: "Manually recorded expenses",
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
             stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
          <circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
          <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
        </svg>
      ),
    },
    {
      key: depositsNeg ? "dep_neg" : "dep",
      label: "Current Deposits",
      value: kpis.currentDeposits,
      desc: "Total payments minus expenses",
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
             stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
        </svg>
      ),
    },
    {
      key: "col",
      label: "Remaining Collectibles",
      value: kpis.remainingCollectibles,
      desc: "Gross total minus total payments",
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
             stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"/>
          <polyline points="12 6 12 12 16 14"/>
        </svg>
      ),
    },
  ];

  return (
    <section className={s.fovCard}>
      <header className={s.fovHead}>
        <div>
          <div className={s.fovEyebrow}>Financial Overview</div>
          <h2 className={s.fovTitle}>School-Year Summary</h2>
          <p className={s.fovSub}>
            {scopeLabel
              ? <>All figures scoped to <b>{scopeLabel}</b>. Expenses reflect the monthly payroll snapshot.</>
              : <>All figures for the current scope. Expenses reflect the monthly payroll snapshot.</>}
          </p>
        </div>
        <div className={s.fovHeadActions}>
          <button
            type="button"
            className={s.fovCompareBtn}
            onClick={onCompare}
            title="Compare your Sales Report Excel against this system data"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" strokeWidth="2.2"
                 strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M18 20V10"/><path d="M12 20V4"/><path d="M6 20v-6"/>
            </svg>
            Compare Excel
          </button>
          <button
            type="button"
            className={s.fovExportBtn}
            onClick={() => exportFinancialReport({ kpis, rawData, activeSy, activeSem })}
            title="Download Excel workbook with full row-level breakdown"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" strokeWidth="2.2"
                 strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="7 10 12 15 17 10"/>
              <line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
            Export to Excel
          </button>
          <div className={s.fovLive} aria-label="Live data">
            <span className={s.fovLiveDot} aria-hidden="true" />
            Live
          </div>
        </div>
      </header>

      {loading ? (
        <div className={s.fovLoading}>Computing totals…</div>
      ) : (
        <div className={s.fovGrid}>
          {tiles.map((tile, i) => (
            <div
              key={tile.key}
              className={`${s.fovTile} ${s[`fovTile_${tile.key}`]}`}
              style={{ animationDelay: `${i * 0.07}s` }}
            >
              <div className={s.fovTileTop}>
                <span className={s.fovTileIcon} aria-hidden="true">{tile.icon}</span>
                <span className={s.fovTileLabel}>{tile.label}</span>
                <button
                  className={s.fovInfoBtn}
                  onClick={e => { e.stopPropagation(); setOpenInfo(tile.key); }}
                  aria-label={`Detail for ${tile.label}`}
                  title={`View ${tile.label} breakdown`}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <circle cx="12" cy="12" r="10"/>
                    <path d="M12 16v-4"/><path d="M12 8h.01"/>
                  </svg>
                </button>
              </div>
              <div className={s.fovTileValue}>{peso(tile.value)}</div>
              <div className={s.fovTileSub}>{tile.desc}</div>
            </div>
          ))}
        </div>
      )}
      {openInfo && tiles.find(t => t.key === openInfo) && (
        <FovInfoModal
          tile={tiles.find(t => t.key === openInfo)}
          onClose={() => setOpenInfo(null)}
          rawData={rawData}
          kpis={kpis}
        />
      )}
    </section>
  );
}

function FinanceSnapshot({
  loading, kpis, daily, activity, studentById, sectionById, sectionFullLabel, invoiceById,
  activeSy, activeSem, s, rawData,
  unpaidByGrade, unpaidMonthOptions, unpaidAsOfMonth, onUnpaidMonthChange, onSelectUnpaidGrade,
}) {
  const monthLabel = new Date().toLocaleDateString(undefined, { month: "long", year: "numeric" });
  const maxDaily = Math.max(1, ...daily.map(d => d.amount));
  const totalLast30 = daily.reduce((sum, d) => sum + d.amount, 0);
  // Scope label: e.g. "2026-2027 · 1st" when both filters are picked, or
  // just "2026-2027" if only the SY is set. Mirrors the pills at the top
  // of the page so the admin sees the same scope reflected here.
  const scopeLabel = [activeSy?.label, activeSem?.label].filter(Boolean).join(" · ");

  // Info-modal state for the two KPI tiles that have a breakdown — reuses
  // the same FovInfoModal + buildFovDetail machinery as the Financial
  // Overview card above, so "Outstanding receivables" and "Remaining
  // Collectibles" are guaranteed to show the identical numbers.
  const [openInfo, setOpenInfo] = useState(null); // "col" | "month"
  const outstandingTile = {
    key: "col",
    label: "Outstanding Receivables",
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
           stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/>
        <polyline points="12 6 12 12 16 14"/>
      </svg>
    ),
  };
  const monthTile = {
    key: "month",
    label: "Collected This Month",
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
           stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="5" width="20" height="14" rx="2"/>
        <line x1="2" y1="10" x2="22" y2="10"/>
      </svg>
    ),
  };

  return (
    <section className={s.financeWrap}>
      <header className={s.financeHead}>
        <div>
          <h2 className={s.financeTitle}>Finance snapshot</h2>
          <p className={s.financeSub}>
            {scopeLabel
              ? <>Cash in and receivables for <b>{scopeLabel}</b> · {monthLabel}. </>
              : <>Cash in and receivables for {monthLabel}. </>}
            Drill down in{" "}
            <a href="/reports" className={s.financeLink}>Reports</a>.
          </p>
        </div>
        {scopeLabel && (
          <div className={s.filterActive}>
            {activeSy  && <span className={s.activePill}>{activeSy.label}</span>}
            {activeSem && <span className={s.activePill}>{activeSem.label}</span>}
          </div>
        )}
      </header>

      <div className={s.financeKpiGrid}>
        <FinanceKpi
          label="Collected this month"
          value={loading ? "…" : peso(kpis.thisMonthCollected)}
          sub={monthLabel}
          tone="ok"
          s={s}
          onInfo={() => setOpenInfo("month")}
        />
        <FinanceKpi
          label="Outstanding receivables"
          value={loading ? "…" : peso(kpis.outstanding)}
          sub="across all unpaid / partial invoices"
          tone={kpis.outstanding > 0.005 ? "warn" : "ok"}
          s={s}
          onInfo={() => setOpenInfo("col")}
        />
      </div>

      <UnpaidByGradeCard
        loading={loading}
        data={unpaidByGrade}
        monthOptions={unpaidMonthOptions}
        selectedMonth={unpaidAsOfMonth}
        onMonthChange={onUnpaidMonthChange}
        onSelectGrade={onSelectUnpaidGrade}
        s={s}
      />

      <div className={s.financePanels}>
        {/* 30-day sparkline — pure SVG, no charting dep. Each day is a bar
            sized against the 30-day max; a dashed baseline and the 30-day
            total sit below so the bars have context. */}
        <div className={s.financePanel}>
          <div className={s.financePanelHead}>
            <div>
              <div className={s.financePanelTitle}>Last 30 days — daily collections</div>
              <div className={s.financePanelSub}>
                Total: <b>{peso(totalLast30)}</b>
              </div>
            </div>
          </div>
          {loading ? (
            <div className={s.financeEmpty}>Loading…</div>
          ) : totalLast30 === 0 ? (
            <div className={s.financeEmpty}>No tuition collected in the last 30 days.</div>
          ) : (
            <div className={s.sparkWrap}>
              <div className={s.sparkBars}>
                {daily.map(d => {
                  const h = Math.max(2, Math.round((d.amount / maxDaily) * 100));
                  return (
                    <div key={d.dayStartMs} className={s.sparkBarCol}
                         title={`${d.label}: ${peso(d.amount)}`}>
                      <div className={s.sparkBar}
                           style={{ height: `${h}%`, opacity: d.amount > 0 ? 1 : 0.15 }} />
                    </div>
                  );
                })}
              </div>
              <div className={s.sparkAxis}>
                <span>{daily[0]?.label}</span>
                <span>{daily[Math.floor(daily.length / 2)]?.label}</span>
                <span>{daily[daily.length - 1]?.label}</span>
              </div>
            </div>
          )}
        </div>

        {/* Recent activity — merged feed of last 10 payments + enrollments
            so the admin sees what the cashier and registrar have been
            doing in one glance. Each event carries its own icon + color. */}
        <div className={s.financePanel}>
          <div className={s.financePanelHead}>
            <div>
              <div className={s.financePanelTitle}>Recent activity</div>
              <div className={s.financePanelSub}>Last 10 payments + enrollments</div>
            </div>
          </div>
          {loading ? (
            <div className={s.financeEmpty}>Loading…</div>
          ) : activity.length === 0 ? (
            <div className={s.financeEmpty}>No activity yet.</div>
          ) : (
            <ul className={s.activityList}>
              {activity.map((ev, i) => {
                const when = new Date(ev.at).toLocaleString(undefined, {
                  month: "short", day: "numeric",
                  hour: "numeric", minute: "2-digit",
                });
                if (ev.kind === "payment") {
                  const p = ev.data;
                  const inv = invoiceById[p.invoiceId];
                  const st = inv ? studentById[inv.studentId] : null;
                  const name = st
                    ? `${st.firstName} ${st.lastName}`.trim()
                    : `Invoice #${p.invoiceId}`;
                  const amt = Number(p.amount || 0);
                  return (
                    <li key={`p-${p.id}-${i}`} className={s.activityRow}>
                      <span className={`${s.activityDot} ${s.activityDotPay}`}>₱</span>
                      <div className={s.activityBody}>
                        <div className={s.activityTitle}>
                          <span className={amt < 0 ? s.activityNeg : s.activityPos}>
                            {amt < 0 ? "−" : ""}{peso(Math.abs(amt))}
                          </span>
                          <span className={s.activityMuted}> · {name}</span>
                        </div>
                        <div className={s.activityMeta}>
                          {p.method || "—"}{p.bankName ? ` · ${p.bankName}` : ""} · {when}
                        </div>
                      </div>
                    </li>
                  );
                }
                // enrollment
                const e = ev.data;
                const st = studentById[e.studentId];
                const sec = e.sectionId ? sectionById[e.sectionId] : null;
                const name = st
                  ? `${st.firstName} ${st.lastName}`.trim()
                  : `Student #${e.studentId}`;
                return (
                  <li key={`e-${e.id}-${i}`} className={s.activityRow}>
                    <span className={`${s.activityDot} ${s.activityDotEnroll}`}>＋</span>
                    <div className={s.activityBody}>
                      <div className={s.activityTitle}>
                        <span>Enrolled</span>
                        <span className={s.activityMuted}> · {name}</span>
                      </div>
                      <div className={s.activityMeta}>
                        {sec ? sectionFullLabel(sec) : "—"} · {when}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      {openInfo === "col" && (
        <FovInfoModal
          tile={outstandingTile}
          onClose={() => setOpenInfo(null)}
          rawData={rawData}
          kpis={kpis}
        />
      )}
      {openInfo === "month" && (
        <FovInfoModal
          tile={monthTile}
          onClose={() => setOpenInfo(null)}
          rawData={rawData}
          kpis={kpis}
        />
      )}
    </section>
  );
}

function FinanceKpi({ label, value, sub, tone, s, onInfo }) {
  const toneClass = tone === "warn" ? s.financeKpiWarn : tone === "ok" ? s.financeKpiOk : "";
  return (
    <div className={`${s.financeKpi} ${toneClass}`}>
      <div className={s.financeKpiTop}>
        <div className={s.financeKpiLabel}>{label}</div>
        {onInfo && (
          <button
            type="button"
            className={s.financeKpiInfoBtn}
            onClick={e => { e.stopPropagation(); onInfo(); }}
            aria-label={`Detail for ${label}`}
            title={`View ${label} breakdown`}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="12" cy="12" r="10"/>
              <path d="M12 16v-4"/><path d="M12 8h.01"/>
            </svg>
          </button>
        )}
      </div>
      <div className={s.financeKpiValue}>{value}</div>
      {sub && <div className={s.financeKpiSub}>{sub}</div>}
    </div>
  );
}

/** Grade-level breakdown of accumulated unpaid balances. Each row drills
 *  into UnpaidGradeModal; each student row there drills into the full
 *  StudentProfileModal (owned by AdminDashboard, not this component, so it
 *  can supply sections/gradeLevels/schoolYears/semesters). */
function UnpaidByGradeCard({ loading, data, monthOptions, selectedMonth, onMonthChange, onSelectGrade, s }) {
  const rows = data?.rows || [];
  const total = data?.total || 0;
  const maxAmount = Math.max(1, ...rows.map(r => r.amount));
  const monthLabel = formatMonthKey(selectedMonth);

  return (
    <section className={s.unpaidCard}>
      <div className={s.unpaidCardHead}>
        <div className={s.unpaidCardHeadText}>
          <div className={s.unpaidCardEyebrow}>Unpaid by Grade Level</div>
          <div className={`${s.unpaidCardTotal} ${total > 0.005 ? s.unpaidCardTotalWarn : s.unpaidCardTotalOk}`}>
            {loading ? "…" : peso(total)}
          </div>
          <p className={s.unpaidCardSub}>
            Accumulated balance still owed, due on or before <b>{monthLabel}</b>.
          </p>
        </div>
        <label className={s.unpaidMonthPicker}>
          <span>As of</span>
          <select
            value={selectedMonth}
            onChange={e => onMonthChange(e.target.value)}
            disabled={loading || monthOptions.length === 0}
          >
            {monthOptions.map(m => (
              <option key={m} value={m}>{formatMonthKey(m)}</option>
            ))}
          </select>
        </label>
      </div>

      {loading ? (
        <div className={s.financeEmpty}>Loading…</div>
      ) : rows.length === 0 ? (
        <div className={s.unpaidEmpty}>
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor"
               strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M20 6L9 17l-5-5"/>
          </svg>
          Every student is paid up through {monthLabel}.
        </div>
      ) : (
        <ul className={s.unpaidGradeList}>
          {rows.map((g, i) => (
            <li
              key={g.gradeId}
              className={s.unpaidGradeItem}
              style={{ animationDelay: `${Math.min(i, 10) * 0.05}s` }}
            >
              <button type="button" className={s.unpaidGradeRow} onClick={() => onSelectGrade(g)}>
                <span className={s.unpaidGradeName}>{g.label}</span>
                <span className={s.unpaidGradeCount}>
                  {g.studentCount} student{g.studentCount === 1 ? "" : "s"}
                </span>
                <span className={s.unpaidGradeBar} aria-hidden="true">
                  <span
                    className={s.unpaidGradeBarFill}
                    style={{ width: `${Math.max(4, Math.round((g.amount / maxAmount) * 100))}%` }}
                  />
                </span>
                <span className={s.unpaidGradeAmount}>{peso(g.amount)}</span>
                <svg className={s.unpaidGradeChevron} width="16" height="16" viewBox="0 0 24 24" fill="none"
                     stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <polyline points="9 18 15 12 9 6"/>
                </svg>
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/** Students in one grade level with an outstanding balance, as of the
 *  month picked on the card above. Clicking a student opens the full
 *  StudentProfileModal (rendered by AdminDashboard, stacked above this). */
const UNPAID_TABLE_DEFAULT_SORT = { key: "remaining", dir: "desc" };

/** Clickable <th> for the UnpaidGradeModal student table. Mirrors SortTh's
 *  behavior (click to sort, click again to flip) with this table's own
 *  class names since it lives outside the fov-modal styling. */
function UnpaidTableTh({ children, sortKey, sort, onSort, defaultDir = "asc", right }) {
  const active = sort.key === sortKey;
  return (
    <th
      className={`${right ? s.unpaidTableThR : ""} ${active ? s.unpaidTableThActive : ""}`.trim()}
      onClick={() => onSort(sortKey, defaultDir)}
      aria-sort={active ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}
    >
      <span className={s.unpaidTableThInner}>
        {children}
        <svg className={s.unpaidTableSortIcon} width="9" height="9" viewBox="0 0 9 9" fill="currentColor" aria-hidden="true">
          {active && sort.dir === "asc"
            ? <path d="M4.5 1.5L8 6.5H1L4.5 1.5Z"/>
            : <path d="M4.5 7.5L1 2.5H8L4.5 7.5Z"/>}
        </svg>
      </span>
    </th>
  );
}

function UnpaidGradeModal({ grade, monthLabel, onClose, onSelectStudent, s }) {
  const [q, setQ] = useState("");
  const [sort, setSort] = useState(UNPAID_TABLE_DEFAULT_SORT);

  useEffect(() => {
    const fn = e => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", fn);
    return () => document.removeEventListener("keydown", fn);
  }, [onClose]);

  const onSort = (key, defaultDir) => setSort(prev =>
    prev.key === key ? { key, dir: prev.dir === "asc" ? "desc" : "asc" } : { key, dir: defaultDir });

  const term = q.trim().toLowerCase();
  const filtered = term
    ? grade.students.filter(row => {
        const name = studentFullName(row.student || {});
        return name.toLowerCase().includes(term)
          || (row.student?.studentNumber || "").toLowerCase().includes(term);
      })
    : grade.students;

  const rows = useMemo(() => {
    const withMeta = filtered.map(row => ({
      ...row,
      _name: studentFullName(row.student || {}),
      _number: row.student?.studentNumber || "",
      _count: row.invoices.length,
    }));
    return sortRows(withMeta, sort);
  }, [filtered, sort]);

  return createPortal(
    <div className={s.modalBackdrop} role="dialog" aria-modal="true" aria-label={`${grade.label} unpaid students`}>
      <div className={s.modalCard} onClick={e => e.stopPropagation()}>
        <div className={s.modalHead}>
          <div>
            <h3 className={s.modalTitle}>{grade.label} — Unpaid</h3>
            <p className={s.modalSubtitle}>
              {grade.studentCount} student{grade.studentCount === 1 ? "" : "s"} · {peso(grade.amount)} total · as of {monthLabel}
            </p>
          </div>
          <button type="button" className={s.modalClose} onClick={onClose}>Close</button>
        </div>

        <div className={s.modalBody}>
          <div className={s.modalSearchWrap}>
            <input
              type="search"
              className={s.modalSearch}
              placeholder="Search by name or student number…"
              value={q}
              onChange={e => setQ(e.target.value)}
              autoFocus
            />
            {term && (
              <span className={s.modalSearchCount}>{filtered.length} of {grade.students.length}</span>
            )}
          </div>

          {rows.length === 0 ? (
            <div className={s.empty}>No students match your search.</div>
          ) : (
            <div className={s.unpaidTableWrap}>
              <table className={s.unpaidTable}>
                <thead><tr>
                  <UnpaidTableTh sortKey="_name" sort={sort} onSort={onSort} defaultDir="asc">Student</UnpaidTableTh>
                  <UnpaidTableTh sortKey="_number" sort={sort} onSort={onSort} defaultDir="asc" right>Student #</UnpaidTableTh>
                  <UnpaidTableTh sortKey="_count" sort={sort} onSort={onSort} defaultDir="desc" right>Invoices</UnpaidTableTh>
                  <UnpaidTableTh sortKey="remaining" sort={sort} onSort={onSort} defaultDir="desc" right>Amount</UnpaidTableTh>
                </tr></thead>
                <tbody>
                  {rows.map(row => {
                    const clickable = !!row.student;
                    return (
                      <tr
                        key={row.studentId}
                        className={`${s.unpaidTr} ${clickable ? "" : s.unpaidTrDisabled}`}
                        onClick={() => clickable && onSelectStudent(row.student)}
                        onKeyDown={e => {
                          if (clickable && (e.key === "Enter" || e.key === " ")) {
                            e.preventDefault();
                            onSelectStudent(row.student);
                          }
                        }}
                        tabIndex={clickable ? 0 : -1}
                        role={clickable ? "button" : undefined}
                        aria-disabled={clickable ? undefined : true}
                        title={clickable ? "View student details" : "Student record not found"}
                      >
                        <td className={s.unpaidTd}>
                          <div className={s.unpaidTdNameCell}>
                            <div className={s.avatar} style={{ background: avatarColor(row._name) }}>
                              {initials(row._name)}
                            </div>
                            <span className={s.unpaidTdName}>{row._name}</span>
                          </div>
                        </td>
                        <td className={`${s.unpaidTd} ${s.unpaidTdR}`}>
                          {row._number ? <span className={s.unpaidTdNumber}>#{row._number}</span> : "—"}
                        </td>
                        <td className={`${s.unpaidTd} ${s.unpaidTdR}`}>{row._count}</td>
                        <td className={`${s.unpaidTd} ${s.unpaidTdR} ${s.unpaidTdAmount}`}>{peso(row.remaining)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className={s.modalFoot}>
          <span>Tip: click a student to view their full profile.</span>
        </div>
      </div>
    </div>,
    document.body
  );
}

function StatTile({ label, value, sub, tone, onClick }) {
  const cls = `${s.stat} ${tone === "warn" ? s.statWarn : ""} ${tone === "ok" ? s.statOk : ""} ${onClick ? s.statClickable : ""}`;
  const body = (
    <>
      <div className={s.statLabel}>{label}</div>
      <div className={s.statValue}>{value}</div>
      {sub && <div className={s.statSub}>{sub}</div>}
      {onClick && <div className={s.statHint}>View details →</div>}
    </>
  );
  if (onClick) {
    return <button type="button" className={cls} onClick={onClick}>{body}</button>;
  }
  return <div className={cls}>{body}</div>;
}

function DrilldownModal({ kind, onClose, onMasterList, ctx }) {
  const { students, teachers, sections, subjects, offerings,
          sectionById, subjectById, employeeById,
          offeringsByTeacher, offeringsBySubject,
          activeSy, activeSem, teachersSorted, subjectsSorted,
          gradeLevelById, sectionFullLabel, user } = ctx;

  // Roles allowed to view the SOA page. SuperDev bypasses all route
  // guards so it is included even though it is not in the route's
  // RequireRole list.
  const canOpenSOA = ["Admin", "SuperAdmin", "SuperDev", "Cashier", "CashierStaff"].includes(user?.role);

  const titles = {
    students:  { title: "Students",        sub: `${students.length} on file` },
    teachers:  { title: "Teachers",        sub: `${teachers.length} teaching staff` },
    sections:  { title: "Sections",        sub: `For ${activeSy?.label || "—"} — ${sections.length} total` },
    subjects:  { title: "Subjects",        sub: `${subjects.length} in catalog` },
    offerings: { title: "Class Offerings", sub: `${offerings.length} for ${activeSy?.label || "—"} · ${activeSem?.label || "—"}` },
    gaps:      { title: "Gaps to fix",     sub: `Unassigned teachers and uncovered subjects for ${activeSem?.label || "—"}` },
  };
  const t = titles[kind] || { title: kind, sub: "" };

  useEffect(() => {
    const fn = e => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", fn);
    return () => document.removeEventListener("keydown", fn);
  }, [onClose]);

  return createPortal(
    <div className={s.modalBackdrop}>
      <div className={s.modalCard} onClick={e => e.stopPropagation()}>
        <div className={s.modalHead}>
          <div>
            <h3 className={s.modalTitle}>{t.title}</h3>
            {t.sub && <p className={s.modalSubtitle}>{t.sub}</p>}
          </div>
          <button type="button" className={s.modalClose} onClick={onClose}>Close</button>
        </div>

        <div className={s.modalBody}>
          {kind === "students" && (
            <StudentsList
              students={students}
              sectionById={sectionById}
              sectionFullLabel={sectionFullLabel}
              canOpenSOA={canOpenSOA}
            />
          )}

          {kind === "teachers" && (
            <TeachersList
              teachers={teachersSorted}
              offeringsByTeacher={offeringsByTeacher}
              subjectById={subjectById}
              sectionById={sectionById}
            />
          )}

          {kind === "sections" && <SectionsList sections={sections} gradeLevelById={gradeLevelById} />}

          {kind === "subjects" && (
            <SubjectsList subjects={subjectsSorted} offeringsBySubject={offeringsBySubject} />
          )}

          {kind === "offerings" && (
            <OfferingsList
              offerings={offerings}
              subjectById={subjectById}
              sectionById={sectionById}
              employeeById={employeeById}
            />
          )}

          {kind === "gaps" && (
            <GapsList
              teachers={teachersSorted}
              subjects={subjectsSorted}
              offeringsByTeacher={offeringsByTeacher}
              offeringsBySubject={offeringsBySubject}
            />
          )}
        </div>

        <div className={s.modalFoot}>
          {kind === "students" && onMasterList && (
            <button type="button" className={s.masterListLink} onClick={onMasterList}>
              View Master List →
            </button>
          )}
          <span>Tip: click outside or press Close to dismiss.</span>
        </div>
      </div>
    </div>,
    document.body
  );
}

function studentFullName(st) {
  return [st.lastName, st.firstName].filter(Boolean).join(", ") || "—";
}

function StudentsList({ students, sectionById, sectionFullLabel, canOpenSOA }) {
  const [q, setQ] = useState("");
  const term = q.trim().toLowerCase();
  const filtered = term
    ? students.filter(st =>
        studentFullName(st).toLowerCase().includes(term) ||
        (st.studentNumber || "").toLowerCase().includes(term)
      )
    : students;

  return (
    <div>
      <div className={s.modalSearchWrap}>
        <input
          type="search"
          className={s.modalSearch}
          placeholder="Search by name or student number…"
          value={q}
          onChange={e => setQ(e.target.value)}
          autoFocus
        />
        {term && (
          <span className={s.modalSearchCount}>
            {filtered.length} of {students.length}
          </span>
        )}
      </div>
      {filtered.length === 0 ? (
        <div className={s.empty}>
          {students.length === 0 ? "No students on file." : "No students match your search."}
        </div>
      ) : (
        <ul className={s.list}>
          {filtered.map(st => {
            const sec = st.currentSectionId ? sectionById[st.currentSectionId] : null;
            const inner = (
              <>
                <div className={s.avatar} style={{ background: avatarColor(studentFullName(st)) }}>
                  {initials(studentFullName(st))}
                </div>
                <div className={s.rowBody}>
                  <div className={s.rowName}>{studentFullName(st)}</div>
                  <div className={s.rowMeta}>
                    {st.studentNumber && <span>#{st.studentNumber}</span>}
                    {sec
                      ? <span>· {sectionFullLabel(sec)}</span>
                      : <span className={s.badgeWarn}>No section</span>}
                    {st.status && st.status !== "ENROLLED" && <span className={s.badgeWarn}>{st.status}</span>}
                    {(!st.status || st.status === "ENROLLED") && <span className={s.badgeOk}>Enrolled</span>}
                  </div>
                </div>
              </>
            );
            // st.id is a server-assigned integer — safe to embed in the URL directly.
            return canOpenSOA ? (
              <li key={st.id}>
                <a
                  href={`/tuition/soa?studentId=${st.id}`}
                  className={s.rowLink}
                  title="Open Statement of Account"
                >
                  {inner}
                </a>
              </li>
            ) : (
              <li key={st.id} className={s.row}>
                {inner}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function TeachersList({ teachers, offeringsByTeacher, subjectById, sectionById }) {
  if (teachers.length === 0) return <div className={s.empty}>No teachers yet.</div>;
  return (
    <ul className={s.list}>
      {teachers.map(t => {
        const load = offeringsByTeacher.get(t.id) || [];
        return (
          <li key={t.id} className={`${s.row} ${load.length === 0 ? s.rowWarn : ""}`}>
            {t.photoUrl
              ? <img src={t.photoUrl} alt="" className={s.avatarImg} />
              : <div className={s.avatar} style={{ background: avatarColor(t.name) }}>{initials(t.name)}</div>}
            <div className={s.rowBody}>
              <div className={s.rowName}>{t.name}</div>
              <div className={s.rowMeta}>
                <span>{t.role || "Teacher"}</span>
                {load.length === 0
                  ? <span className={s.badgeWarn}>Unassigned</span>
                  : <span className={s.badgeOk}>{load.length} class{load.length === 1 ? "" : "es"}</span>}
              </div>
              {load.length > 0 && (
                <div className={s.pillRow}>
                  {load.map(o => {
                    const subj = subjectById[o.subjectId];
                    const sec  = sectionById[o.sectionId];
                    return (
                      <span key={o.id} className={s.pill}>
                        <span className={s.pillCode}>{subj?.code || "?"}</span>
                        <span className={s.pillSep}>·</span>
                        <span>{sec?.name || "?"}</span>
                      </span>
                    );
                  })}
                </div>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function SectionsList({ sections, gradeLevelById }) {
  if (sections.length === 0) return <div className={s.empty}>No sections in this school year.</div>;
  const groups = new Map();
  for (const sec of sections) {
    const key = sec.gradeLevelId ?? "ungraded";
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(sec);
  }
  const entries = Array.from(groups.entries()).sort((a, b) => {
    const ka = a[0] === "ungraded" ? Infinity : Number(a[0]);
    const kb = b[0] === "ungraded" ? Infinity : Number(b[0]);
    return ka - kb;
  });
  return (
    <>
      {entries.map(([gid, list]) => {
        const grade = gid === "ungraded" ? null : gradeLevelById[gid];
        return (
          <div key={gid}>
            <div className={s.modalGroupHead}>
              {grade ? grade.name : "Unassigned grade"} — {list.length} section{list.length === 1 ? "" : "s"}
            </div>
            <ul className={s.list}>
              {list.map(sec => (
                <li key={sec.id} className={s.row}>
                  <div className={s.rowBody}>
                    <div className={s.rowName}>{sec.name}</div>
                    <div className={s.rowMeta}>
                      {sec.adviserStaffId
                        ? <span className={s.badgeOk}>Adviser set</span>
                        : <span className={s.badgeWarn}>No adviser</span>}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </>
  );
}

function SubjectsList({ subjects, offeringsBySubject }) {
  if (subjects.length === 0) return <div className={s.empty}>No subjects in the catalog.</div>;
  return (
    <ul className={s.list}>
      {subjects.map(x => {
        const offs = offeringsBySubject.get(x.id) || [];
        return (
          <li key={x.id} className={`${s.row} ${offs.length === 0 ? s.rowWarn : ""}`}>
            <div className={s.subjPill}>{x.code}</div>
            <div className={s.rowBody}>
              <div className={s.rowName}>{x.title}</div>
              <div className={s.rowMeta}>
                {x.units != null && <span>{x.units} units</span>}
                {offs.length === 0
                  ? <span className={s.badgeWarn}>Not offered</span>
                  : <span className={s.badgeOk}>{offs.length} offering{offs.length === 1 ? "" : "s"}</span>}
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function OfferingsList({ offerings, subjectById, sectionById, employeeById }) {
  if (offerings.length === 0) return <div className={s.empty}>No class offerings this semester.</div>;
  return (
    <ul className={s.list}>
      {offerings.map(o => {
        const subj = subjectById[o.subjectId];
        const sec  = sectionById[o.sectionId];
        const teacher = employeeById[o.teacherStaffId];
        return (
          <li key={o.id} className={s.row}>
            <div className={s.subjPill}>{subj?.code || "?"}</div>
            <div className={s.rowBody}>
              <div className={s.rowName}>{subj?.title || `Subject #${o.subjectId}`}</div>
              <div className={s.rowMeta}>
                <span>{sec?.name || "—"}</span>
                {teacher
                  ? <span>· {teacher.name}</span>
                  : <span className={s.badgeWarn}>No teacher</span>}
                {o.schedule && <span>· {o.schedule}</span>}
                {o.room && <span>· Room {o.room}</span>}
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function GapsList({ teachers, subjects, offeringsByTeacher, offeringsBySubject }) {
  const unassigned = teachers.filter(t => (offeringsByTeacher.get(t.id) || []).length === 0);
  const uncovered  = subjects.filter(x => (offeringsBySubject.get(x.id) || []).length === 0);
  if (unassigned.length === 0 && uncovered.length === 0) {
    return <div className={s.empty}>No gaps — every teacher has a class and every subject is offered.</div>;
  }
  return (
    <>
      {unassigned.length > 0 && (
        <>
          <div className={s.modalGroupHead}>Unassigned teachers ({unassigned.length})</div>
          <ul className={s.list}>
            {unassigned.map(t => (
              <li key={t.id} className={`${s.row} ${s.rowWarn}`}>
                <div className={s.avatar} style={{ background: avatarColor(t.name) }}>{initials(t.name)}</div>
                <div className={s.rowBody}>
                  <div className={s.rowName}>{t.name}</div>
                  <div className={s.rowMeta}>
                    <span>{t.role || "Teacher"}</span>
                    <span className={s.badgeWarn}>No classes</span>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </>
      )}
      {uncovered.length > 0 && (
        <>
          <div className={s.modalGroupHead}>Uncovered subjects ({uncovered.length})</div>
          <ul className={s.list}>
            {uncovered.map(x => (
              <li key={x.id} className={`${s.row} ${s.rowWarn}`}>
                <div className={s.subjPill}>{x.code}</div>
                <div className={s.rowBody}>
                  <div className={s.rowName}>{x.title}</div>
                  <div className={s.rowMeta}>
                    {x.units != null && <span>{x.units} units</span>}
                    <span className={s.badgeWarn}>Not offered</span>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </>
      )}
    </>
  );
}
